/**
 * Opportunity Portfolio Component
 * Shows ranked opportunities with CAPEX stratification, MACC curves, tCO2e reduction, AI Report, and PDF Export
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  BarChart3, Loader2, RefreshCw, Download, TrendingUp,
  DollarSign, Clock, Leaf, Award, ArrowLeft, Info, Filter,
  FileText, Sparkles, X, CheckCircle, AlertTriangle,
  ChevronDown, ChevronRight, Layers, List, Zap, Settings
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, ReferenceLine, ScatterChart, Scatter, ZAxis
} from 'recharts';
import {
  generateBrandedPdf,
  fetchTenantLogoDataUrl,
  PLATFORM_ATTRIBUTION,
  AI_DISCLAIMER,
} from '../../utils/pdfBranding';

const API = process.env.REACT_APP_BACKEND_URL;

// Grid emission factor (India CEA Baseline) - kgCO2 per kWh
const EMISSION_FACTOR = 0.716;

// CAPEX tier thresholds (INR)
const CAPEX_TIERS = {
  'No CAPEX': { min: 0, max: 10000, color: '#22c55e', bgColor: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-800' },
  'Low CAPEX': { min: 10000, max: 500000, color: '#3b82f6', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-800' },
  'Medium CAPEX': { min: 500000, max: 2500000, color: '#f59e0b', bgColor: 'bg-amber-50', borderColor: 'border-amber-200', textColor: 'text-amber-800' },
  'High CAPEX': { min: 2500000, max: Infinity, color: '#ef4444', bgColor: 'bg-red-50', borderColor: 'border-red-200', textColor: 'text-red-800' },
};

const getCapexTier = (capexAvg) => {
  if (capexAvg <= 10000) return 'No CAPEX';
  if (capexAvg <= 500000) return 'Low CAPEX';
  if (capexAvg <= 2500000) return 'Medium CAPEX';
  return 'High CAPEX';
};

const OpportunityPortfolioV3 = ({ study, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [portfolioData, setPortfolioData] = useState(null);
  const [activeCapexFilter, setActiveCapexFilter] = useState('All');
  const [seuData, setSeuData] = useState([]);

  // View toggle: Recommended (best opportunity per significant SEU, default)
  // vs Detail (one row per SEU-opportunity pair).
  const [viewMode, setViewMode] = useState('recommended');

  // Recommended-list sort order.
  const [recSort, setRecSort] = useState('savings'); // 'bee' | 'savings' | 'payback'

  // BEE-aligned scoring toggle (on = condition/benchmark-aware score + ranking).
  const beeKey = study?.id ? `portfolio-bee-${study.id}` : null;
  const [beeScoring, setBeeScoring] = useState(() => {
    if (beeKey) { const s = localStorage.getItem(beeKey); if (s !== null) return s === 'true'; }
    return true;
  });
  const toggleBee = () => {
    setBeeScoring((prev) => { const next = !prev; if (beeKey) localStorage.setItem(beeKey, String(next)); return next; });
  };

  // SEU condition assessment (Phase 2) — feeds equipment-health + confidence.
  const [showAssess, setShowAssess] = useState(false);
  const [assessDraft, setAssessDraft] = useState({});   // seu_id -> {name, seu_code, efficiency, age, maintenance, automation}
  const [assessSaving, setAssessSaving] = useState(false);

  const setAssessField = (id, field, val) =>
    setAssessDraft((p) => ({ ...p, [id]: { ...p[id], [field]: val } }));

  const openAssess = async () => {
    // Seed one row per significant SEU, then overlay any stored values.
    const seed = {};
    bestPerSeu.forEach((c) => {
      if (!c.seu_id) return;
      seed[c.seu_id] = {
        name: c.equipment_type || c.equipment_name || c.seu_id,
        seu_code: c.seu_code || '',
        efficiency: '', age: '', maintenance: '', automation: '',
      };
    });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/api/opportunity-v3/matching/study/${study.id}/seu-assessment`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const stored = res.data?.assessments || {};
      Object.entries(stored).forEach(([id, a]) => {
        if (!seed[id]) seed[id] = { name: id, seu_code: '', efficiency: '', age: '', maintenance: '', automation: '' };
        seed[id].efficiency = a.existing_efficiency != null ? Math.round(a.existing_efficiency * 100) : '';
        seed[id].age = a.equipment_age_years ?? '';
        seed[id].maintenance = a.maintenance_score != null ? Math.round(a.maintenance_score * 100) : '';
        seed[id].automation = a.automation_level || '';
      });
    } catch (e) { /* start blank */ }
    setAssessDraft(seed);
    setShowAssess(true);
  };

  const saveAssess = async () => {
    setAssessSaving(true);
    try {
      const token = localStorage.getItem('token');
      const assessments = Object.entries(assessDraft).map(([seu_id, d]) => {
        const filled = [d.efficiency, d.age, d.maintenance, d.automation].filter((v) => v !== '' && v != null);
        return {
          seu_id,
          existing_efficiency: d.efficiency !== '' && d.efficiency != null ? Number(d.efficiency) / 100 : null,
          equipment_age_years: d.age !== '' && d.age != null ? Number(d.age) : null,
          maintenance_score: d.maintenance !== '' && d.maintenance != null ? Number(d.maintenance) / 100 : null,
          automation_level: d.automation || null,
          data_quality: Math.min(1, filled.length / 4),
        };
      });
      await axios.put(
        `${API}/api/opportunity-v3/matching/study/${study.id}/seu-assessment`,
        { assessments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAssess(false);
      fetchPortfolioData();   // re-score with the new condition data
    } catch (e) {
      // keep the modal open on failure
    } finally {
      setAssessSaving(false);
    }
  };

  // Backend default-filters unviable rows (payback >= 15 yr or ROI < 0).
  // Toggle shows them back for admin/debug viewing.
  const [showUnviable, setShowUnviable] = useState(false);

  // Tariff overrides — persisted per-study in localStorage so a customer on
  // ₹10.5/kWh doesn't have to re-enter it every session. Empty string means
  // "use backend default" (rate is not sent as a query param).
  const tariffStorageKey = study?.id ? `portfolio-tariffs-${study.id}` : null;
  const [tariffs, setTariffs] = useState(() => {
    if (tariffStorageKey) {
      try {
        const saved = JSON.parse(localStorage.getItem(tariffStorageKey) || '{}');
        return {
          electricity: saved.electricity ?? '',
          png: saved.png ?? '',
          diesel: saved.diesel ?? '',
          lpg: saved.lpg ?? '',
        };
      } catch { /* ignore malformed */ }
    }
    return { electricity: '', png: '', diesel: '', lpg: '' };
  });
  const [pendingTariffs, setPendingTariffs] = useState(tariffs);
  // Single expand flag for the merged "Customization" panel (tariffs + selection).
  const [customizeExpanded, setCustomizeExpanded] = useState(false);


  // AI Report state
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiReport, setAiReport] = useState(null);
  const [showAiReport, setShowAiReport] = useState(false);

  // PDF state
  const [pdfLoading, setPdfLoading] = useState(false);

  // Per-opportunity what/how/implement narrative for the Recommended cards.
  const [narratives, setNarratives] = useState({});
  const [narrativeLoading, setNarrativeLoading] = useState({});

  // Fetch (and cache) the narrative for one opportunity, seeding any authored
  // text already on the calc. Used by the Recommended card view.
  const loadNarrative = async (calc) => {
    const id = calc?.capex_item_id;
    if (!id || narratives[id] || narrativeLoading[id]) return;
    if (calc.description || calc.how_it_helps || calc.implementation_notes) {
      setNarratives((p) => ({ ...p, [id]: {
        description: calc.description, how_it_helps: calc.how_it_helps,
        implementation_notes: calc.implementation_notes } }));
    }
    if (!calc.description || !calc.how_it_helps || !calc.implementation_notes) {
      setNarrativeLoading((p) => ({ ...p, [id]: true }));
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/api/opportunity-v3/matching/opportunity-narrative`,
          { capex_item_id: id, site_id: study?.site_id || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setNarratives((p) => ({ ...p, [id]: res.data }));
      } catch (e) {
        setNarratives((p) => ({ ...p, [id]: { error: true } }));
      } finally {
        setNarrativeLoading((p) => ({ ...p, [id]: false }));
      }
    }
  };

  const chartRef = useRef(null);
  const beeMatrixRef = useRef(null);   // Consumption-vs-Opportunity chart, for the PDF

  useEffect(() => {
    if (study?.id) {
      fetchPortfolioData();
      fetchSeuData();
    }
    // Tariff / BEE-toggle dependency is deliberate → refetch
  }, [study?.id, showUnviable, tariffs, beeScoring]);

  const fetchPortfolioData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = { include_unviable: showUnviable };
    // Only send a tariff param if user has set it; blank means use backend default.
    if (tariffs.electricity !== '' && tariffs.electricity != null) params.electricity_rate = Number(tariffs.electricity);
    if (tariffs.png !== '' && tariffs.png != null) params.png_rate_gj = Number(tariffs.png);
    if (tariffs.diesel !== '' && tariffs.diesel != null) params.diesel_rate_gj = Number(tariffs.diesel);
    if (tariffs.lpg !== '' && tariffs.lpg != null) params.lpg_rate_gj = Number(tariffs.lpg);
    // BEE-aligned scoring toggle → condition/benchmark-aware score + ranking.
    params.bee_scoring = beeScoring;

    try {
      const res = await axios.post(
        `${API}/api/opportunity-v3/matching/study/${study.id}/calculate`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, params }
      );

      // Backend now supplies tco2e_reduction_annual / tco2e_reduction_lifetime
      // computed with resource-aware emission factors (grid EF for electrical,
      // per-fuel EF for thermal). We still need to compute abatement cost and
      // CAPEX tier client-side because those depend on lifecycle_years +
      // mid-CAPEX which the UI displays directly.
      const calculationsWithExtras = res.data.calculations?.map(calc => {
        const avgCapex = (calc.estimated_capex_low + calc.estimated_capex_high) / 2;
        const lifecycle = calc.lifecycle_years || 10;
        const lifetimeTco2e = calc.tco2e_reduction_lifetime
          ?? ((calc.tco2e_reduction_annual || 0) * lifecycle);
        const lifetimeSavings = calc.estimated_annual_savings_inr * lifecycle;
        const netCost = avgCapex - lifetimeSavings;
        const abatementCost = lifetimeTco2e > 0 ? netCost / lifetimeTco2e : 0;

        return {
          ...calc,
          // Keep a kWh-equivalent figure for legacy UI references; for fuel
          // SEUs this is GJ (same numeric, different unit) and the field
          // `energy_savings_unit` carries the true unit.
          energy_savings_kwh: Math.round(calc.energy_savings_value || 0),
          tco2e_reduction_annual: calc.tco2e_reduction_annual || 0,
          tco2e_reduction_lifetime: lifetimeTco2e,
          abatement_cost: Math.round(abatementCost),
          avg_capex: avgCapex,
          capex_tier: getCapexTier(avgCapex),
        };
      }) || [];
      
      const totalTco2eAnnual = calculationsWithExtras.reduce((sum, c) => sum + (c.tco2e_reduction_annual || 0), 0);
      const totalTco2eLifetime = calculationsWithExtras.reduce((sum, c) => sum + (c.tco2e_reduction_lifetime || 0), 0);
      
      const tierCounts = { 'No CAPEX': 0, 'Low CAPEX': 0, 'Medium CAPEX': 0, 'High CAPEX': 0 };
      calculationsWithExtras.forEach(c => {
        tierCounts[c.capex_tier]++;
      });
      
      setPortfolioData({
        ...res.data,
        calculations: calculationsWithExtras,
        summary: {
          ...res.data.summary,
          total_tco2e_annual: Math.round(totalTco2eAnnual * 10) / 10,
          total_tco2e_lifetime: Math.round(totalTco2eLifetime),
          tier_counts: tierCounts,
        }
      });
      
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeuData = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(
        `${API}/api/opportunity-v3/matching/study/${study.id}/seu-data`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSeuData(res.data.seus || []);
    } catch (error) {
      console.error('Error fetching SEU data:', error);
    }
  };

  // Filter calculations by CAPEX tier
  const filteredCalculations = useMemo(() => {
    if (!portfolioData?.calculations) return [];
    if (activeCapexFilter === 'All') return portfolioData.calculations;
    return portfolioData.calculations.filter(c => c.capex_tier === activeCapexFilter);
  }, [portfolioData, activeCapexFilter]);

  // The single best (viable, highest-priority) opportunity per significant
  // energy use — the curated "Recommended" set: one card per SEU equipment.
  const bestPerSeu = useMemo(() => {
    if (!filteredCalculations.length) return [];
    const bySeu = new Map();
    const rankVal = (x) => (x.bee_score != null ? x.bee_score : (x.priority_score || 0));
    const better = (a, b) => {
      if (!b) return true;
      const av = a.viable ? 1 : 0, bv = b.viable ? 1 : 0;
      if (av !== bv) return av > bv;
      // Prefer BEE score when available, else priority_score, else savings.
      if (rankVal(a) !== rankVal(b)) return rankVal(a) > rankVal(b);
      return (a.estimated_annual_savings_inr || 0) > (b.estimated_annual_savings_inr || 0);
    };
    filteredCalculations.forEach((c) => {
      const key = c.seu_id || c.seu_code || c.equipment_name || c.capex_item_id;
      if (better(c, bySeu.get(key))) bySeu.set(key, c);
    });
    const sortVal = (c) => {
      if (recSort === 'savings') return c.estimated_annual_savings_inr || 0;
      if (recSort === 'payback') return -(c.simple_payback_years || 999); // shorter first
      return rankVal(c); // 'bee'
    };
    return Array.from(bySeu.values()).sort((a, b) => sortVal(b) - sortVal(a));
  }, [filteredCalculations, recSort]);

  // Roll-up of the recommended set (best opportunity per SEU) — drives the
  // top summary cards so they reflect what would actually be implemented,
  // not the sum of every matched opportunity.
  const recommendedSummary = useMemo(() => {
    const items = bestPerSeu;
    const paybacks = items.map(c => c.simple_payback_years).filter(v => v != null && v > 0);
    return {
      total_capex_low: items.reduce((s, c) => s + (c.estimated_capex_low || 0), 0),
      total_capex_high: items.reduce((s, c) => s + (c.estimated_capex_high || 0), 0),
      total_annual_savings: items.reduce((s, c) => s + (c.estimated_annual_savings_inr || 0), 0),
      total_tco2e_annual: Math.round(items.reduce((s, c) => s + (c.tco2e_reduction_annual || 0), 0) * 10) / 10,
      avg_payback_years: paybacks.length ? paybacks.reduce((a, b) => a + b, 0) / paybacks.length : null,
      count: items.length,
    };
  }, [bestPerSeu]);

  // BEE insights (Phase 3 dashboards): funnel, SEU 2x2 matrix, category mix.
  // Derived from the recommended set (best opportunity per SEU).
  const beeInsights = useMemo(() => {
    const items = bestPerSeu;
    const scored = items.filter(c => c.bee_score != null);
    const funnel = {
      total: items.length,
      viable: items.filter(c => c.viable).length,
      quickWins: items.filter(c => c.opportunity_category === 'Quick Win').length,
      capital: items.filter(c => c.opportunity_category === 'Capital Investment').length,
    };
    // Energy-share axis: use seu_share_percent when populated; otherwise derive
    // it from each SEU's annual energy spend (comparable across resources) so
    // the axis isn't collapsed to 0 when share% wasn't captured.
    const anyShare = scored.some(c => (Number(c.seu_share_percent) || 0) > 0);
    const totalCost = scored.reduce((s, c) => s + (Number(c.annual_energy_cost_inr) || 0), 0) || 1;
    const shareDerived = !anyShare;
    const matrix = scored.map(c => ({
      x: anyShare
        ? (Number(c.seu_share_percent) || 0)
        : ((Number(c.annual_energy_cost_inr) || 0) / totalCost * 100),
      y: c.bee_score,
      z: Math.max(4, (c.estimated_annual_savings_inr || 0) / 100000),  // bubble ~ savings (L)
      name: c.equipment_type || c.equipment_name || c.seu_code || 'SEU',
      category: c.opportunity_category,
      color: c.bee_score >= 66 ? '#2f7d4f' : c.bee_score >= 40 ? '#b8860b' : '#64748b',
    }));
    const catCounts = {};
    items.forEach(c => { const k = c.opportunity_category || 'Uncategorised'; catCounts[k] = (catCounts[k] || 0) + 1; });
    const avgScore = scored.length ? Math.round(scored.reduce((s, c) => s + c.bee_score, 0) / scored.length) : null;
    const shareMedian = matrix.length ? [...matrix.map(m => m.x)].sort((a, b) => a - b)[Math.floor(matrix.length / 2)] : 10;
    return { funnel, matrix, catCounts, avgScore, hasScores: scored.length > 0, shareMedian: Math.max(shareMedian, 2), shareDerived };
  }, [bestPerSeu]);

  // Heatmap: SEU (rows) x opportunity category (cols), cell = best bee_score.
  // Uses the full matched set (every opportunity is BEE-scored when the flag is on).
  const beeHeatmap = useMemo(() => {
    const scored = filteredCalculations.filter((c) => c.bee_score != null);
    if (!scored.length) return { categories: [], rows: [] };
    const order = ['Quick Win', 'Low Cost', 'Operational Improvement', 'Medium Investment', 'Capital Investment', 'No Opportunity'];
    const distinct = Array.from(new Set(scored.map((c) => c.opportunity_category || 'Uncategorised')));
    const categories = [...order.filter((c) => distinct.includes(c)), ...distinct.filter((c) => !order.includes(c))];
    const byRow = new Map();
    scored.forEach((c) => {
      const key = c.seu_id || c.seu_code || c.equipment_name;
      const name = c.equipment_type || c.equipment_name || c.seu_code || 'SEU';
      if (!byRow.has(key)) byRow.set(key, { name, cells: {} });
      const cells = byRow.get(key).cells;
      const cat = c.opportunity_category || 'Uncategorised';
      if (cells[cat] == null || c.bee_score > cells[cat]) cells[cat] = c.bee_score;
    });
    return { categories, rows: Array.from(byRow.values()) };
  }, [filteredCalculations]);

  // Auto-generate/cache the narrative for each recommended card when the
  // Recommended view is active (fetch-only; loadNarrative dedupes/caches).
  useEffect(() => {
    if (viewMode === 'recommended') {
      bestPerSeu.forEach((c) => loadNarrative(c));
    }
  }, [viewMode, bestPerSeu]);

  // The dataset that matches the active toggle — drives the tier cards + MACC
  // so they stay in sync with the table:
  //   recommended → best opportunity per SEU
  //   summary     → grouped by opportunity
  //   detail      → every per-asset calculation
  const viewItems = useMemo(() => (
    viewMode === 'recommended' ? bestPerSeu : filteredCalculations
  ), [viewMode, bestPerSeu, filteredCalculations]);

  // MACC data: driven by the active view (recommended / summary / detail).
  // All feed the same bar chart shape (name, abatementCost, tco2e, tierColor).
  const maccData = useMemo(() => {
    const source = viewItems;
    if (!source.length) return [];

    const sorted = [...source]
      .filter(c => c.tco2e_reduction_lifetime > 0)
      .sort((a, b) => a.abatement_cost - b.abatement_cost);

    let cumulativeWidth = 0;
    return sorted.map((calc, idx) => {
      const startX = cumulativeWidth;
      cumulativeWidth += calc.tco2e_reduction_lifetime;
      const label = calc.opportunity_name || calc.capex_item_name || `Opp ${idx + 1}`;
      const fullName = viewMode === 'recommended'
        ? `${calc.opportunity_name} — ${calc.equipment_type || calc.equipment_name || 'N/A'}`
        : `${calc.capex_item_name} (${calc.equipment_type || 'N/A'})`;
      return {
        name: label.substring(0, 28),
        fullName,
        abatementCost: calc.abatement_cost,
        tco2e: calc.tco2e_reduction_lifetime,
        cumulativeWidth,
        startX,
        payback: calc.simple_payback_years,
        capexTier: calc.capex_tier,
        tierColor: CAPEX_TIERS[calc.capex_tier]?.color || '#6b7280',
      };
    });
  }, [viewMode, viewItems]);

  // Robust Y-axis domain for the MACC chart.
  //
  // Problem: one outlier (e.g. a high-CAPEX item matched to a SEU with tiny
  // annual savings) can have an abatement cost of ₹100M+/tCO₂e, which stretches
  // the Y-axis so much that every other bar collapses to a sub-pixel sliver.
  // Fix: clip the visible Y range to the 5th–95th percentile with 15% padding.
  // Outlier bars still render but get capped at the top/bottom edge — far
  // better than losing the main signal.
  const maccYDomain = useMemo(() => {
    if (maccData.length === 0) return ['auto', 'auto'];
    const costs = maccData.map(d => d.abatementCost).sort((a, b) => a - b);
    const percentile = (p) => {
      if (costs.length === 1) return costs[0];
      const idx = Math.min(
        costs.length - 1,
        Math.max(0, Math.floor((costs.length - 1) * p))
      );
      return costs[idx];
    };
    const lo = percentile(0.05);
    const hi = percentile(0.95);
    // If 95th percentile still looks extreme vs median, fall back to ±5×median
    const median = percentile(0.50);
    const medianAbs = Math.abs(median) || 1;
    const clippedHi = Math.min(hi, Math.abs(median) + medianAbs * 10);
    const clippedLo = Math.max(lo, median - medianAbs * 10);
    const span = Math.max(clippedHi - clippedLo, medianAbs * 2, 1000);
    const pad = span * 0.15;
    return [Math.round(clippedLo - pad), Math.round(clippedHi + pad)];
  }, [maccData]);

  const maccOutlierCount = useMemo(() => {
    if (maccData.length === 0) return 0;
    const [lo, hi] = maccYDomain;
    if (lo === 'auto') return 0;
    return maccData.filter(d => d.abatementCost < lo || d.abatementCost > hi).length;
  }, [maccData, maccYDomain]);

  // Generate AI Report
  const generateAiReport = async () => {
    setAiReportLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API}/api/opportunity-v3/matching/study/${study.id}/portfolio-ai-report`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setAiReport(res.data.report);
        setShowAiReport(true);
      } else {
        alert('Error generating AI report: ' + (res.data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error generating AI report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiReportLoading(false);
    }
  };

  // Generate PDF Report — uses html2pdf.js for elegant rendering matching Final Report
  const generatePdfReport = async () => {
    setPdfLoading(true);
    const pdfStyleId = 'portfolio-pdf-style';

    try {
      const { default: html2canvas } = await import('html2canvas');
      const tenantLogoDataUrl = await fetchTenantLogoDataUrl();
      const calcs = portfolioData?.calculations || [];
      // Report leads with the recommended set (best opportunity per significant
      // SEU) — mirrors the on-screen Recommended view.
      const rec = bestPerSeu;
      const recSum = recommendedSummary;

      // Ensure a what/benefits/implementation narrative is available for each
      // recommended opportunity (seed from state, fetch+cache any missing).
      const narrativeMap = {};
      await Promise.all(rec.map(async (c) => {
        const id = c.capex_item_id;
        if (!id) return;
        if (narratives[id] && !narratives[id].error) { narrativeMap[id] = narratives[id]; return; }
        let n = (c.description || c.how_it_helps || c.implementation_notes)
          ? { description: c.description, how_it_helps: c.how_it_helps, implementation_notes: c.implementation_notes }
          : {};
        if (!c.description || !c.how_it_helps || !c.implementation_notes) {
          try {
            const token = localStorage.getItem('token');
            const res = await axios.post(
              `${API}/api/opportunity-v3/matching/opportunity-narrative`,
              { capex_item_id: id, site_id: study?.site_id || null },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            n = { ...n, ...res.data };
          } catch { /* keep authored/empty */ }
        }
        narrativeMap[id] = n;
      }));

      // Significant SEUs actually used for matching (subset of all SEUs).
      const recSeuCodes = new Set(rec.map(c => c.seu_code).filter(Boolean));
      const significantSeus = seuData.filter(s => recSeuCodes.has(s.seu_code));
      const seuRows = significantSeus.length ? significantSeus : seuData;

      // Tier summary — over the recommended set (matches the on-screen cards).
      const tierSummaries = Object.entries(CAPEX_TIERS).map(([tier, config]) => {
        const tierOpps = rec.filter(c => c.capex_tier === tier);
        return {
          tier, color: config.color,
          count: tierOpps.length,
          savings: tierOpps.reduce((s, c) => s + (c.estimated_annual_savings_inr || 0), 0),
          tco2e: tierOpps.reduce((s, c) => s + (c.tco2e_reduction_annual || 0), 0),
        };
      });

      // Capture MACC chart as image
      let chartImgSrc = '';
      if (chartRef.current) {
        try {
          const canvas = await html2canvas(chartRef.current, { scale: 2, backgroundColor: '#ffffff' });
          chartImgSrc = canvas.toDataURL('image/png');
        } catch (e) { console.warn('Chart capture failed:', e); }
      }

      // Capture the Consumption-vs-Opportunity bubble chart (only in the DOM
      // when the Recommended view + BEE scoring are active).
      let matrixImgSrc = '';
      if (beeMatrixRef.current) {
        try {
          const canvas = await html2canvas(beeMatrixRef.current, { scale: 2, backgroundColor: '#ffffff' });
          matrixImgSrc = canvas.toDataURL('image/png');
        } catch (e) { console.warn('Matrix capture failed:', e); }
      }

      // Build printable HTML
      const container = document.createElement('div');
      container.id = 'portfolio-pdf-content';
      container.innerHTML = `
        <style>
          #portfolio-pdf-content {
            font-family: Arial, Helvetica, sans-serif;
            color: #1f2937; font-size: 13px; line-height: 1.6;
            max-width: 800px; margin: 0 auto;
          }
          #portfolio-pdf-content h1 { font-size: 26px; color: #4338ca; margin-bottom: 6px; }
          #portfolio-pdf-content h2 { font-size: 18px; color: #4338ca; border-bottom: 2px solid #6366f1; padding-bottom: 5px; margin: 24px 0 12px; }
          #portfolio-pdf-content h3 { font-size: 15px; color: #374151; margin: 12px 0 8px; }
          #portfolio-pdf-content table { width: 100%; border-collapse: collapse; font-size: 11px; }
          #portfolio-pdf-content th { background: #f3f4f6; padding: 7px 8px; text-align: left; font-weight: 600; border: 1px solid #e5e7eb; }
          #portfolio-pdf-content td { padding: 6px 8px; border: 1px solid #e5e7eb; overflow-wrap: anywhere; word-break: break-word; vertical-align: top; }
          #portfolio-pdf-content tr:nth-child(even) { background: #f9fafb; }
          #portfolio-pdf-content .top3 { background: #dcfce7 !important; }
          #portfolio-pdf-content .cover { text-align: center; padding: 60px 0 40px; min-height: 272mm; display: flex; flex-direction: column; justify-content: center; align-items: center; }
          #portfolio-pdf-content .cover .subtitle { color: #6b7280; font-size: 16px; }
          #portfolio-pdf-content .cover .date { color: #9ca3af; font-size: 13px; margin-top: 8px; }
          #portfolio-pdf-content .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 12px 0; }
          #portfolio-pdf-content .summary-item { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; }
          #portfolio-pdf-content .summary-item .label { font-size: 11px; color: #6b7280; }
          #portfolio-pdf-content .summary-item .value { font-size: 18px; font-weight: 700; color: #1f2937; }
          #portfolio-pdf-content .tier-bar { display: flex; align-items: center; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 13px; }
          #portfolio-pdf-content .tier-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
          #portfolio-pdf-content .chart-img { width: 100%; max-height: 280px; object-fit: contain; margin: 10px 0; }
          #portfolio-pdf-content .rec-item { padding: 6px 0 6px 12px; border-left: 3px solid #6366f1; margin: 6px 0; font-size: 12px; }
          #portfolio-pdf-content .rec-block { border: 1px solid #e5e7eb; border-left: 4px solid #6366f1; border-radius: 6px; padding: 10px 12px; margin: 10px 0; page-break-inside: avoid; }
          #portfolio-pdf-content .rec-block .rec-head { font-size: 13px; color: #111827; margin-bottom: 2px; }
          #portfolio-pdf-content .rec-block .tier { font-size: 10px; color: #6b7280; background: #f3f4f6; border-radius: 4px; padding: 1px 6px; margin-left: 6px; }
          #portfolio-pdf-content .rec-block .rec-meta { font-size: 11px; color: #6b7280; margin-bottom: 6px; }
          #portfolio-pdf-content .rec-block .narr { font-size: 12px; margin: 2px 0; }
          #portfolio-pdf-content .rec-block .narr .lbl { font-weight: 700; color: #374151; }
          #portfolio-pdf-content .footer { text-align: center; color: #9ca3af; font-size: 10px; margin-top: 30px; padding: 12px 0 20px; border-top: 1px solid #e5e7eb; }
          #portfolio-pdf-content .page-break { page-break-before: always; break-before: page; }
        </style>

        <!-- Cover Page (tenant branding) -->
        <div class="cover">
          ${tenantLogoDataUrl
            ? `<img src="${tenantLogoDataUrl}" alt="Company logo" style="max-height:80px;max-width:240px;object-fit:contain;display:inline-block;margin-bottom:24px;" />`
            : `<div style="display:inline-flex;align-items:center;justify-content:center;width:240px;height:80px;border:2px dashed #d1d5db;border-radius:8px;background:#f9fafb;margin-bottom:24px;"><span style="font-size:14px;font-style:italic;color:#9ca3af;">Your logo here</span></div>`}
          <h1>Opportunity Portfolio Report</h1>
          <p class="subtitle">${study?.name || 'Energy Study'}</p>
          <p class="date">Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>

        <!-- Executive Summary (page-break ensures it starts on its own page after the cover) -->
        <div class="page-break">
          <h2>1. Executive Summary</h2>
          <p style="font-size:12px;color:#6b7280;margin:0 0 10px;">Figures reflect the recommended opportunities — the best opportunity for each significant energy use.</p>
          <div class="summary-grid">
            <div class="summary-item"><div class="label">Recommended Opportunities</div><div class="value">${recSum.count || 0}</div></div>
            <div class="summary-item"><div class="label">Total CAPEX Range</div><div class="value">&#8377;${(recSum.total_capex_low / 100000)?.toFixed(1) || 0}L &ndash; &#8377;${(recSum.total_capex_high / 100000)?.toFixed(1) || 0}L</div></div>
            <div class="summary-item"><div class="label">Annual Savings Potential</div><div class="value">&#8377;${(recSum.total_annual_savings / 100000)?.toFixed(1) || 0}L</div></div>
            <div class="summary-item"><div class="label">CO&#8322;e Reduction (Annual)</div><div class="value">${recSum.total_tco2e_annual?.toFixed(1) || 0} tCO&#8322;e</div></div>
            <div class="summary-item"><div class="label">Average Payback</div><div class="value">${recSum.avg_payback_years?.toFixed(1) || 'N/A'} years</div></div>
            <div class="summary-item"><div class="label">Significant Energy Uses</div><div class="value">${rec.length}</div></div>
          </div>

          <!-- CAPEX Tier Summary -->
          <h2>2. CAPEX Tier Breakdown</h2>
          ${tierSummaries.map(t => `
            <div class="tier-bar">
              <span class="tier-dot" style="background:${t.color}"></span>
              <strong>${t.tier}</strong>: ${t.count} opportunities &bull; &#8377;${(t.savings / 100000).toFixed(1)}L savings/yr &bull; ${t.tco2e.toFixed(1)} tCO&#8322;e/yr
            </div>
          `).join('')}
        </div>

        <!-- SEU Table -->
        <div class="page-break">
          <h2>3. Significant Energy Uses (SEUs)</h2>
          <p style="font-size:12px;color:#6b7280;margin:0 0 8px;">The energy uses driving the site's consumption, used to match opportunities.</p>
          <table>
            <thead><tr><th>Equipment</th><th>Type</th><th>Capacity</th><th>Load %</th><th>Annual Energy</th></tr></thead>
            <tbody>
              ${seuRows.map(s => `
                <tr>
                  <td>${(s.equipment_name || s.equipment_type || 'N/A').substring(0, 30)}</td>
                  <td>${s.resource_type || '-'}</td>
                  <td>${s.rated_capacity || '-'} ${s.rated_capacity_unit || 'kW'}</td>
                  <td>${s.load_percent || '-'}%</td>
                  <td>${s.annual_energy ? (s.annual_energy / 1000).toFixed(0) + ' MWh' : '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <!-- Recommended Opportunities (best per SEU) with narratives -->
        <div class="page-break">
          <h2>4. Recommended Opportunities</h2>
          <p style="font-size:12px;color:#6b7280;margin:0 0 10px;">One recommended opportunity per significant energy use, with what it is, how it helps, and how to implement it.</p>
          ${matrixImgSrc
            ? `<div style="margin:0 0 14px;"><h3 style="font-size:14px;color:#374151;margin:0 0 6px;">Consumption vs Opportunity</h3><img src="${matrixImgSrc}" alt="Consumption vs Opportunity" style="width:100%;max-height:300px;object-fit:contain;border:1px solid #e5e7eb;border-radius:6px;" /></div>`
            : ''}
          ${rec.length > 0 ? rec.map((c, i) => {
            const n = narrativeMap[c.capex_item_id] || {};
            const beeChip = c.bee_score != null
              ? `<span class="tier" style="background:#eef2ff;color:#4338ca;">BEE ${Math.round(c.bee_score)}/100${c.confidence_pct != null ? ' · ' + Math.round(c.confidence_pct) + '% conf' : ''}</span>`
              : '';
            const catChip = c.opportunity_category ? `<span class="tier">${c.opportunity_category}</span>` : '';
            const reasons = Array.isArray(c.reasons) && c.reasons.length
              ? `<div class="rec-meta">${c.reasons.map(r => '&#10003; ' + r).join(' &nbsp; ')}</div>` : '';
            return `
              <div class="rec-block">
                <div class="rec-head"><strong>${i + 1}. ${c.opportunity_name || '-'}</strong><span class="tier">${c.capex_tier}</span>${catChip}${beeChip}</div>
                <div class="rec-meta">
                  For ${(c.equipment_type || c.equipment_name || 'N/A')}${c.seu_code ? ' · ' + c.seu_code : ''}
                  &bull; CAPEX &#8377;${((c.estimated_capex_low || 0) / 100000).toFixed(1)}&ndash;${((c.estimated_capex_high || 0) / 100000).toFixed(1)}L
                  &bull; Savings &#8377;${((c.estimated_annual_savings_inr || 0) / 100000).toFixed(2)}L/yr
                  &bull; Payback ${c.simple_payback_years ? c.simple_payback_years + ' yr' : '—'}
                  &bull; ${(c.tco2e_reduction_annual || 0).toFixed(1)} tCO&#8322;e/yr
                </div>
                ${reasons}
                <div class="narr"><span class="lbl">Overview:</span> ${n.description || '—'}</div>
                <div class="narr"><span class="lbl">Benefits:</span> ${n.how_it_helps || '—'}</div>
                <div class="narr"><span class="lbl">Implementation:</span> ${n.implementation_notes || '—'}</div>
              </div>`;
          }).join('') : '<p style="color:#9ca3af;">No recommended opportunities for the current selection.</p>'}
        </div>

        <!-- MACC Chart -->
        <div class="page-break">
          <h2>5. Marginal Abatement Cost Curve (MACC)</h2>
          ${chartImgSrc
            ? `<img class="chart-img" src="${chartImgSrc}" alt="MACC Chart" />`
            : '<p style="color:#9ca3af;text-align:center;padding:40px 0;">MACC chart not available</p>'}
          <p style="font-size:11px;color:#6b7280;margin-top:6px;">
            Each bar = one opportunity. Height = cost per tCO&#8322;e reduced. Width = tCO&#8322;e potential. Bars below zero = net saving.
          </p>
        </div>

        <!-- Full Opportunity List (all matched opportunities for the significant SEUs) -->
        <div class="page-break">
          <h2>6. Full Opportunity List</h2>
          <p style="font-size:12px;color:#6b7280;margin:0 0 8px;">All matched opportunities across the significant energy uses, ranked by priority.</p>
          <table>
            <thead><tr><th>#</th><th>Tier</th><th>Opportunity</th><th>Equipment</th><th>CAPEX</th><th>Savings/yr</th><th>Payback</th><th>tCO&#8322;e/yr</th></tr></thead>
            <tbody>
              ${calcs.map((c, i) => `
                <tr class="${i < 3 ? 'top3' : ''}">
                  <td>${c.rank}</td>
                  <td>${c.capex_tier}</td>
                  <td>${c.capex_item_name || '-'}</td>
                  <td>${c.equipment_type || c.equipment_name || '-'}</td>
                  <td>&#8377;${(c.avg_capex / 1000)?.toFixed(0) || 0}K</td>
                  <td>&#8377;${(c.estimated_annual_savings_inr / 100000)?.toFixed(2) || 0}L</td>
                  <td>${c.simple_payback_years ? c.simple_payback_years + ' yr' : '-'}</td>
                  <td>${c.tco2e_reduction_annual?.toFixed(1) || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="footer" style="text-align:center;color:#6b7280;font-size:10px;margin-top:30px;padding:14px 0 16px;border-top:1px solid #e5e7eb;page-break-inside:avoid;">
          <p style="margin:0 0 4px 0;">${PLATFORM_ATTRIBUTION}</p>
          <p style="margin:0;font-style:italic;color:#9ca3af;">${AI_DISCLAIMER}</p>
        </div>
      `;

      // Render off-screen for layout / image+font load only.
      container.style.cssText = 'position:absolute; left:-10000px; top:0; width:900px; background:#ffffff;';
      document.body.appendChild(container);

      // Wait for images/fonts to load
      await new Promise(resolve => setTimeout(resolve, 500));

      try {
        await generateBrandedPdf({
          element: container,
          filename: `Opportunity_Portfolio_${study?.name?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`,
          // Body has its own cover + footer; helper adds the per-page stamps only.
          skipCover: true,
          skipLastPage: true,
        });
      } finally {
        if (container.parentElement) document.body.removeChild(container);
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF: ' + error.message);
      // Clean up if container was added
      const leftover = document.getElementById('portfolio-pdf-content');
      if (leftover) leftover.remove();
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!portfolioData?.calculations) return;
    
    const headers = [
      'Rank', 'CAPEX Tier', 'SEU Code', 'Equipment', 'Opportunity', 'CAPEX Item',
      'CAPEX Low (INR)', 'CAPEX High (INR)', 'Annual Savings (INR)',
      'Payback (Years)', 'ROI %', 'tCO2e/Year', 'Lifetime tCO2e',
      'Abatement Cost (INR/tCO2e)', 'Priority Score'
    ];
    
    const rows = filteredCalculations.map(c => [
      c.rank, c.capex_tier, c.seu_code, c.equipment_type, c.opportunity_name, c.capex_item_name,
      c.estimated_capex_low, c.estimated_capex_high, c.estimated_annual_savings_inr,
      c.simple_payback_years, c.roi_percent, c.tco2e_reduction_annual, c.tco2e_reduction_lifetime,
      c.abatement_cost, c.priority_score
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opportunity_portfolio_${study.id}.csv`;
    a.click();
  };

  // Custom MACC tooltip
  const MACCTooltip = ({ active, payload }) => {
    if (active && payload && payload[0]) {
      const data = payload[0].payload;
      const isCostSaving = data.abatementCost < 0;
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg max-w-xs">
          <p className="font-semibold text-gray-900 text-sm mb-1">{data.fullName}</p>
          <div className="space-y-1 text-xs">
            <p className={isCostSaving ? 'text-green-600 font-medium' : 'text-gray-600'}>
              Abatement Cost: {isCostSaving ? '−' : ''}₹{Math.abs(data.abatementCost)?.toLocaleString()}/tCO₂e
              {isCostSaving && ' (Net Saving)'}
            </p>
            <p className="text-gray-600">Lifetime tCO₂e: {data.tco2e?.toLocaleString()}</p>
            <p className="text-gray-600">Payback: {data.payback || 'N/A'} years</p>
            <p className="text-gray-500">Tier: {data.capexTier}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Generating portfolio and MACC analysis...</p>
        </div>
      </div>
    );
  }

  if (!portfolioData || !portfolioData.calculations?.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">No Calculations Available</h3>
        <p className="text-gray-600 mb-4">
          Complete SEU matching first to generate the opportunity portfolio.
        </p>
        <button onClick={onBack} className="text-indigo-600 hover:underline" data-testid="go-to-match-btn">
          Go to Match & Calculate
        </button>
      </div>
    );
  }

  const summary = portfolioData.summary || {};
  const tierCounts = summary.tier_counts || {};

  return (
    <div className="space-y-4" data-testid="opportunity-portfolio">
      {/* SEU Condition Assessment modal (BEE Phase 2) */}
      {showAssess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Equipment Condition</h3>
                <p className="text-sm text-gray-500">Sharpens the BEE score & confidence. Blank fields fall back to BEE benchmarks.</p>
              </div>
              <button onClick={() => setShowAssess(false)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-3">Equipment</th>
                      <th className="py-2 px-2">Efficiency %</th>
                      <th className="py-2 px-2">Age (yrs)</th>
                      <th className="py-2 px-2">Maintenance %</th>
                      <th className="py-2 pl-2">Automation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {Object.entries(assessDraft).map(([id, d]) => (
                      <tr key={id}>
                        <td className="py-2 pr-3">
                          <div className="font-medium text-gray-800">{d.name}</div>
                          {d.seu_code && <div className="text-xs text-gray-400">{d.seu_code}</div>}
                        </td>
                        <td className="py-2 px-2"><input type="number" min="0" max="100" value={d.efficiency} onChange={(e) => setAssessField(id, 'efficiency', e.target.value)} className="w-20 px-2 py-1 border border-gray-300 rounded" placeholder="—" /></td>
                        <td className="py-2 px-2"><input type="number" min="0" step="0.5" value={d.age} onChange={(e) => setAssessField(id, 'age', e.target.value)} className="w-20 px-2 py-1 border border-gray-300 rounded" placeholder="—" /></td>
                        <td className="py-2 px-2"><input type="number" min="0" max="100" value={d.maintenance} onChange={(e) => setAssessField(id, 'maintenance', e.target.value)} className="w-20 px-2 py-1 border border-gray-300 rounded" placeholder="—" /></td>
                        <td className="py-2 pl-2">
                          <select value={d.automation} onChange={(e) => setAssessField(id, 'automation', e.target.value)} className="px-2 py-1 border border-gray-300 rounded">
                            <option value="">—</option>
                            <option value="manual">Manual</option>
                            <option value="basic">Basic</option>
                            <option value="automated">Automated</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Lower efficiency vs the BEE benchmark, older age and poorer maintenance all raise the opportunity score.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button onClick={() => setShowAssess(false)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={saveAssess} disabled={assessSaving} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:bg-indigo-300 inline-flex items-center gap-2">
                {assessSaving && <Loader2 className="w-4 h-4 animate-spin" />} Save & Rescore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sector-filter warning — explains an empty result (fail-closed matching) */}
      {portfolioData?.sector_filter_warning && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{portfolioData.sector_filter_warning}</p>
        </div>
      )}

      {/* AI Report Modal */}
      {showAiReport && aiReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6" />
                <h3 className="text-lg font-bold">AI Portfolio Analysis Report</h3>
              </div>
              <button onClick={() => setShowAiReport(false)} className="p-1 hover:bg-white/20 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Handle parse error / raw response */}
              {aiReport.parse_error ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-bold text-amber-900 mb-2">AI Report (Raw Response)</h4>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{aiReport.raw_response || 'No response received.'}</p>
                </div>
              ) : (<>
              {/* Executive Summary */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" /> Executive Summary
                </h4>
                <p className="text-gray-700 leading-relaxed">{typeof aiReport.executive_summary === 'string' ? aiReport.executive_summary : JSON.stringify(aiReport.executive_summary)}</p>
              </div>

              {/* Key Findings */}
              {Array.isArray(aiReport.key_findings) && aiReport.key_findings.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Key Findings</h4>
                  <ul className="space-y-2">
                    {aiReport.key_findings.map((finding, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <span className="text-indigo-500 mt-1">→</span>
                        {typeof finding === 'string' ? finding : (finding?.name ? `${finding.name}${finding.equipment ? ` (${finding.equipment})` : ''}${finding.savings ? ` — ₹${Number(finding.savings).toLocaleString()} savings` : ''}` : JSON.stringify(finding))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CAPEX Strategy */}
              {aiReport.capex_strategy && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">CAPEX Strategy Recommendations</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-semibold text-green-800 mb-2">No CAPEX Quick Wins</h5>
                      <ul className="text-sm text-green-700 space-y-1">
                        {aiReport.capex_strategy.no_capex_quick_wins?.map((item, i) => (
                          <li key={i}>• {typeof item === 'string' ? item : (item?.name || JSON.stringify(item))}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-800 mb-2">Low CAPEX Short-Term</h5>
                      <ul className="text-sm text-blue-700 space-y-1">
                        {aiReport.capex_strategy.low_capex_short_term?.map((item, i) => (
                          <li key={i}>• {typeof item === 'string' ? item : (item?.name || JSON.stringify(item))}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h5 className="font-semibold text-amber-800 mb-2">Medium/High CAPEX Long-Term</h5>
                      <ul className="text-sm text-amber-700 space-y-1">
                        {aiReport.capex_strategy.medium_high_capex_long_term?.map((item, i) => (
                          <li key={i}>• {typeof item === 'string' ? item : (item?.name || JSON.stringify(item))}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Implementation Roadmap */}
              {aiReport.implementation_roadmap && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">Implementation Roadmap</h4>
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-indigo-200"></div>
                    {[
                      { phase: 'Phase 1 (0-6 months)', items: aiReport.implementation_roadmap.phase_1_0_6_months, color: 'bg-green-500' },
                      { phase: 'Phase 2 (6-12 months)', items: aiReport.implementation_roadmap.phase_2_6_12_months, color: 'bg-blue-500' },
                      { phase: 'Phase 3 (12-24 months)', items: aiReport.implementation_roadmap.phase_3_12_24_months, color: 'bg-purple-500' }
                    ].map((phase, idx) => (
                      <div key={idx} className="relative pl-10 pb-6">
                        <div className={`absolute left-2 w-5 h-5 rounded-full ${phase.color} border-4 border-white`}></div>
                        <h5 className="font-semibold text-gray-900 mb-2">{phase.phase}</h5>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {phase.items?.map((item, i) => (
                            <li key={i}>• {typeof item === 'string' ? item : (item?.name || JSON.stringify(item))}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Assessment */}
              {Array.isArray(aiReport.risk_assessment) && aiReport.risk_assessment.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" /> Risk Assessment
                  </h4>
                  <div className="space-y-3">
                    {aiReport.risk_assessment.map((risk, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3">
                        <p className="font-medium text-gray-800">{typeof risk === 'string' ? risk : (risk?.risk || JSON.stringify(risk))}</p>
                        {risk?.mitigation && <p className="text-sm text-gray-600 mt-1"><strong>Mitigation:</strong> {String(risk.mitigation)}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Conclusion */}
              {aiReport.conclusion && (
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Conclusion</h4>
                  <p className="text-gray-700">{typeof aiReport.conclusion === 'string' ? aiReport.conclusion : JSON.stringify(aiReport.conclusion)}</p>
                </div>
              )}

              {/* Priority Actions */}
              {Array.isArray(aiReport.priority_actions) && aiReport.priority_actions.length > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h4 className="font-bold text-indigo-900 mb-2">Top 3 Priority Actions</h4>
                  <ol className="list-decimal list-inside text-indigo-800 space-y-1">
                    {aiReport.priority_actions.map((action, idx) => (
                      <li key={idx}>{typeof action === 'string' ? action : (action?.name || JSON.stringify(action))}</li>
                    ))}
                  </ol>
                </div>
              )}
              </>)}
            </div>
          </div>
        </div>
      )}

      {/* Back Button & Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-sm"
          data-testid="back-to-match-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Match & Calculate
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={generateAiReport}
            disabled={aiReportLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 text-sm"
            data-testid="ai-report-btn"
          >
            {aiReportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            AI Report
          </button>
          <button
            onClick={generatePdfReport}
            disabled={pdfLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 text-sm"
            data-testid="pdf-report-btn"
          >
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            PDF Report
          </button>
          <button
            onClick={downloadCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
          <button
            onClick={fetchPortfolioData}
            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-3">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">Recommended</span>
          </div>
          <p className="text-2xl font-bold">{recommendedSummary.count}</p>
          <p className="text-[11px] opacity-80 mt-0.5">one per significant energy use</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">Total CAPEX</span>
          </div>
          <p className="text-lg font-bold">
            ₹{(recommendedSummary.total_capex_low / 100000)?.toFixed(1)}L - {(recommendedSummary.total_capex_high / 100000)?.toFixed(1)}L
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">Annual Savings</span>
          </div>
          <p className="text-lg font-bold">₹{(recommendedSummary.total_annual_savings / 100000)?.toFixed(1)}L</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Leaf className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">tCO₂e/Year</span>
          </div>
          <p className="text-2xl font-bold">{recommendedSummary.total_tco2e_annual?.toLocaleString()}</p>
        </div>

        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">Avg Payback</span>
          </div>
          <p className="text-2xl font-bold">{recommendedSummary.avg_payback_years?.toFixed(1) || '-'} yr</p>
        </div>
      </div>

      {/* Customization — energy tariffs (per-study; blank = backend default). */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => { setPendingTariffs(tariffs); setCustomizeExpanded(!customizeExpanded); }}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Settings className="w-5 h-5 text-gray-500" />
            <span className="font-semibold text-gray-900">Customization</span>
            <span className="text-xs text-gray-500 ml-2">
              Tariffs — Elec {portfolioData?.tariffs?.electricity_rate_inr_per_kwh ?? 8}/kWh · PNG {portfolioData?.tariffs?.png_rate_inr_per_gj ?? 1450}/GJ · Diesel {portfolioData?.tariffs?.diesel_rate_inr_per_gj ?? 2400}/GJ · LPG {portfolioData?.tariffs?.lpg_rate_inr_per_gj ?? 1780}/GJ
            </span>
            {(tariffs.electricity || tariffs.png || tariffs.diesel || tariffs.lpg) && (
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full ml-2">customized</span>
            )}
          </div>
          {customizeExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
        </button>
        {customizeExpanded && (
          <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-5">
            {/* Tariffs */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-800">Energy Tariffs</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Electricity (₹/kWh)</label>
                  <input type="number" step="0.01" placeholder="8.00"
                    value={pendingTariffs.electricity}
                    onChange={(e) => setPendingTariffs({ ...pendingTariffs, electricity: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">PNG / Natural Gas (₹/GJ)</label>
                  <input type="number" step="10" placeholder="1450"
                    value={pendingTariffs.png}
                    onChange={(e) => setPendingTariffs({ ...pendingTariffs, png: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Diesel / HSD (₹/GJ)</label>
                  <input type="number" step="10" placeholder="2400"
                    value={pendingTariffs.diesel}
                    onChange={(e) => setPendingTariffs({ ...pendingTariffs, diesel: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">LPG (₹/GJ)</label>
                  <input type="number" step="10" placeholder="1780"
                    value={pendingTariffs.lpg}
                    onChange={(e) => setPendingTariffs({ ...pendingTariffs, lpg: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                </div>
              </div>
            </div>

            {/* Apply / Reset */}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => {
                  setTariffs(pendingTariffs);
                  if (tariffStorageKey) localStorage.setItem(tariffStorageKey, JSON.stringify(pendingTariffs));
                  setCustomizeExpanded(false);
                }}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
              >
                Apply & Recalculate
              </button>
              <button
                onClick={() => {
                  const clearedTariffs = { electricity: '', png: '', diesel: '', lpg: '' };
                  setPendingTariffs(clearedTariffs); setTariffs(clearedTariffs);
                  if (tariffStorageKey) localStorage.removeItem(tariffStorageKey);
                }}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
              >
                Reset to defaults
              </button>
              <span className="text-xs text-gray-500 ml-2">
                Blank fields use backend defaults. Applies to this study only.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* CAPEX Tier Filter */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="font-semibold text-gray-900">Filter by CAPEX Tier</span>
          </div>
          <div className="flex items-center gap-4">
            <label
              className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
              title={`Show opportunities with payback ≥ ${portfolioData.viability_threshold_years || 15} yr or negative ROI. These are algorithmic fallout (high CAPEX paired with tiny-energy SEUs) and are hidden by default.`}
            >
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={showUnviable}
                onChange={(e) => setShowUnviable(e.target.checked)}
              />
              Show unviable ({(portfolioData.total_opportunities_unfiltered || 0) - (portfolioData.total_opportunities || 0)})
            </label>
            <span className="text-sm text-gray-500">
              Showing {filteredCalculations.length} of {portfolioData.total_opportunities} opportunities
              {portfolioData.total_opportunities_unfiltered != null && portfolioData.total_opportunities_unfiltered !== portfolioData.total_opportunities && (
                <span className="text-gray-400">
                  {' '}· {portfolioData.total_opportunities_unfiltered - portfolioData.total_opportunities} unviable hidden
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCapexFilter('All')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeCapexFilter === 'All'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid="filter-all"
          >
            All ({portfolioData.total_opportunities})
          </button>
          {Object.entries(CAPEX_TIERS).map(([tier, config]) => (
            <button
              key={tier}
              onClick={() => setActiveCapexFilter(tier)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                activeCapexFilter === tier
                  ? `${config.bgColor} ${config.textColor} ${config.borderColor} border-2`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-transparent'
              }`}
              data-testid={`filter-${tier.toLowerCase().replace(' ', '-')}`}
            >
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }}></span>
              {tier} ({tierCounts[tier] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* MACC Curve */}
      <div className="bg-white rounded-xl border border-gray-200 p-4" ref={chartRef}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-900">Marginal Abatement Cost Curve (MACC)</h3>
            <p className="text-sm text-gray-500">Ordered by cost-effectiveness (most cost-effective on left)</p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span className="text-gray-600">Below line = Net Saving</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-400 rounded"></div>
              <span className="text-gray-600">Above line = Net Cost</span>
            </div>
          </div>
        </div>
        
        {maccData.length > 0 ? (
          <>
            {maccOutlierCount > 0 && (
              <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
                Y-axis clipped to 5th–95th percentile for readability.
                {' '}<strong>{maccOutlierCount}</strong> outlier {maccOutlierCount === 1 ? 'bar extends' : 'bars extend'} beyond the visible range — see the ranked table below for exact values.
              </div>
            )}
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={maccData}
                  margin={{ top: 20, right: 30, left: 60, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    type="category"
                    interval={maccData.length > 30 ? Math.ceil(maccData.length / 20) : 0}
                    angle={-35}
                    textAnchor="end"
                    height={70}
                    tick={{ fontSize: 10 }}
                    label={{ value: 'Opportunities (ordered by cost-effectiveness)', position: 'bottom', offset: 50, style: { fontSize: 12 } }}
                  />
                  <YAxis
                    domain={maccYDomain}
                    allowDataOverflow
                    tickFormatter={(v) => {
                      const abs = Math.abs(v);
                      const sign = v < 0 ? '−' : '';
                      if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(1)}Cr`;
                      if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(1)}L`;
                      if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(0)}k`;
                      return `${sign}₹${abs.toFixed(0)}`;
                    }}
                    label={{ value: 'Abatement Cost (₹/tCO₂e)', angle: -90, position: 'insideLeft', offset: -45, style: { fontSize: 12 } }}
                  />
                  <Tooltip content={<MACCTooltip />} />
                  <ReferenceLine y={0} stroke="#000" strokeWidth={2} />
                  <Bar
                    dataKey="abatementCost"
                    name="Abatement Cost"
                    maxBarSize={60}
                  >
                    {maccData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.tierColor}
                        stroke={entry.abatementCost < 0 ? '#16a34a' : '#dc2626'}
                        strokeWidth={entry.abatementCost < 0 ? 2 : 0}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-500">
            No data available for MACC curve with current filter
          </div>
        )}
        
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <strong>Reading the MACC:</strong> Each bar represents an opportunity. 
              <strong> Height</strong> = cost per tCO₂e reduced (negative = net saving after payback). 
              <strong> Width</strong> = tCO₂e reduction potential. 
              Bars <strong>below</strong> the zero line are cost-effective (savings exceed CAPEX over lifecycle).
            </div>
          </div>
        </div>
      </div>

      {/* CAPEX Tier Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        {Object.entries(CAPEX_TIERS).map(([tier, config]) => {
          const tierOpps = viewItems.filter(c => c.capex_tier === tier);
          const tierSavings = tierOpps.reduce((s, c) => s + (c.estimated_annual_savings_inr || 0), 0);
          const tierTco2e = tierOpps.reduce((s, c) => s + (c.tco2e_reduction_annual || 0), 0);
          
          return (
            <div key={tier} className={`${config.bgColor} ${config.borderColor} border rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }}></span>
                <span className={`font-semibold ${config.textColor}`}>{tier}</span>
              </div>
              <div className="space-y-1 text-sm">
                <p className="text-gray-600">{tierOpps.length} opportunities</p>
                <p className="text-gray-600">₹{(tierSavings / 100000).toFixed(1)}L savings/yr</p>
                <p className="text-gray-600">{tierTco2e.toFixed(1)} tCO₂e/yr</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ranked Opportunities Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <h4 className="font-semibold text-gray-900">
              {viewMode === 'recommended' ? 'Recommended Opportunities' : 'Ranked Opportunities'}
            </h4>
            <span className="text-sm text-gray-500">
              {viewMode === 'recommended'
                ? `${bestPerSeu.length} recommendation${bestPerSeu.length === 1 ? '' : 's'} · one per significant energy use`
                : `${filteredCalculations.length} per-asset rows · sorted by priority`}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* BEE scoring toggle */}
            <button
              onClick={toggleBee}
              role="switch"
              aria-checked={beeScoring}
              title="Rank & score opportunities using BEE-aligned intelligence (savings potential, equipment condition, BEE priority)"
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm transition-colors ${
                beeScoring ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">BEE scoring</span>
              <span className={`relative inline-block w-8 h-4 rounded-full transition-colors ${beeScoring ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${beeScoring ? 'left-4' : 'left-0.5'}`}></span>
              </span>
            </button>

            <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden text-sm">
              <button
                onClick={() => setViewMode('recommended')}
                className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors ${
                  viewMode === 'recommended'
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                title="Top recommendation per significant energy use, explained"
              >
                <Sparkles className="w-4 h-4" /> Recommended
              </button>
              <button
                onClick={() => setViewMode('detail')}
                className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors border-l border-gray-200 ${
                  viewMode === 'detail'
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                title="One row per asset (implementation tracker)"
              >
                <List className="w-4 h-4" /> Detail
              </button>
            </div>
          </div>
        </div>

        {/* Recommended card view: SEU equipments + the top opportunity for each */}
        {viewMode === 'recommended' && (
          <div className="p-4 sm:p-5">
            <p className="text-sm text-gray-600 mb-4">
              Based on your significant energy uses, here are the recommended opportunities for your industry and equipment.
            </p>

            {/* Significant Energy Uses banner */}
            <div className="flex flex-wrap items-center gap-2 mb-5 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mr-1">Significant Energy Uses</span>
              {bestPerSeu.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 text-xs">
                  <Zap className="w-3.5 h-3.5 text-amber-500" />
                  {c.equipment_type || c.equipment_name}
                </span>
              ))}
              {bestPerSeu.length === 0 && <span className="text-sm text-gray-500">No significant energy uses matched yet.</span>}
              {bestPerSeu.length > 0 && (
                <div className="ml-auto flex items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                    <span>Sort by</span>
                    <select
                      value={recSort}
                      onChange={(e) => setRecSort(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-xs bg-white"
                    >
                      <option value="savings">Highest savings</option>
                      <option value="bee">BEE score</option>
                      <option value="payback">Fastest payback</option>
                    </select>
                  </label>
                  {beeScoring && (
                    <button
                      onClick={openAssess}
                      title="Enter equipment condition (efficiency, age, maintenance) to sharpen the BEE score & confidence"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-indigo-200 bg-white text-indigo-700 text-xs font-medium hover:bg-indigo-50"
                    >
                      <Settings className="w-3.5 h-3.5" /> Condition data
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* BEE Insights: funnel + consumption-vs-opportunity matrix + mix */}
            {beeScoring && beeInsights.hasScores && (
              <div className="mb-5 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Opportunities', v: beeInsights.funnel.total, cls: 'bg-slate-50 text-slate-700 border-slate-200' },
                    { label: 'Viable', v: beeInsights.funnel.viable, cls: 'bg-blue-50 text-blue-700 border-blue-200' },
                    { label: 'Quick Wins', v: beeInsights.funnel.quickWins, cls: 'bg-green-50 text-green-700 border-green-200' },
                    { label: 'Capital Projects', v: beeInsights.funnel.capital, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
                  ].map((f, i) => (
                    <div key={i} className={`rounded-lg border px-3 py-2 ${f.cls}`}>
                      <div className="text-xl font-bold leading-none">{f.v}</div>
                      <div className="text-[11px] uppercase tracking-wide mt-1">{f.label}</div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2 border border-gray-200 rounded-xl p-3" ref={beeMatrixRef}>
                    <div className="flex items-center justify-between mb-1">
                      <h5 className="text-sm font-semibold text-gray-800">Consumption vs Opportunity</h5>
                      {beeInsights.avgScore != null && <span className="text-xs text-gray-500">avg score {beeInsights.avgScore}/100</span>}
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                      <ScatterChart margin={{ top: 10, right: 16, bottom: 24, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                        <XAxis type="number" dataKey="x" name="Energy share" unit="%" tick={{ fontSize: 11 }} label={{ value: beeInsights.shareDerived ? 'Energy share % (est. from spend)' : 'Energy share %', position: 'insideBottom', offset: -12, fontSize: 11 }} />
                        <YAxis type="number" dataKey="y" name="BEE score" domain={[0, 100]} tick={{ fontSize: 11 }} label={{ value: 'BEE score', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                        <ZAxis type="number" dataKey="z" range={[40, 400]} />
                        <ReferenceLine y={50} stroke="#cbd5e1" strokeDasharray="4 4" />
                        <ReferenceLine x={beeInsights.shareMedian} stroke="#cbd5e1" strokeDasharray="4 4" />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
                          if (!active || !payload || !payload.length) return null;
                          const p = payload[0].payload;
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg shadow px-2 py-1 text-xs">
                              <div className="font-medium text-gray-800">{p.name}</div>
                              <div className="text-gray-500">Score {Math.round(p.y)}/100 · {p.x.toFixed(0)}% share</div>
                              <div className="text-gray-500">{p.category}</div>
                            </div>
                          );
                        }} />
                        <Scatter data={beeInsights.matrix}>
                          {beeInsights.matrix.map((m, i) => <Cell key={i} fill={m.color} />)}
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Bubble size ≈ annual savings. Top-left = low consumption / high opportunity (surface first); bottom-right = high consumption / low opportunity.
                      {beeInsights.shareDerived && ' Energy share is estimated from annual energy spend (SEU share % not captured).'}
                    </p>
                  </div>

                  <div className="border border-gray-200 rounded-xl p-3">
                    <h5 className="text-sm font-semibold text-gray-800 mb-2">Opportunity mix</h5>
                    <div className="space-y-1.5">
                      {Object.entries(beeInsights.catCounts).sort((a, b) => b[1] - a[1]).map(([cat, n]) => (
                        <div key={cat} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{cat}</span>
                          <span className="font-semibold text-gray-900">{n}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Heatmap: SEU x opportunity category, cell = best BEE score */}
                {beeHeatmap.rows.length > 0 && (
                  <div className="border border-gray-200 rounded-xl p-3">
                    <h5 className="text-sm font-semibold text-gray-800 mb-2">Opportunity heatmap</h5>
                    <div className="overflow-x-auto">
                      <table className="text-xs border-collapse">
                        <thead>
                          <tr>
                            <th className="text-left font-medium text-gray-500 px-2 py-1 sticky left-0 bg-white">Equipment</th>
                            {beeHeatmap.categories.map((cat) => (
                              <th key={cat} className="font-medium text-gray-500 px-2 py-1 whitespace-nowrap text-center">{cat}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {beeHeatmap.rows.map((row, ri) => (
                            <tr key={ri}>
                              <td className="text-left text-gray-700 px-2 py-1 whitespace-nowrap sticky left-0 bg-white">{row.name}</td>
                              {beeHeatmap.categories.map((cat) => {
                                const v = row.cells[cat];
                                const bg = v == null ? 'transparent' : v >= 66 ? '#dcfce7' : v >= 40 ? '#fef3c7' : '#f1f5f9';
                                const fg = v == null ? '#cbd5e1' : v >= 66 ? '#166534' : v >= 40 ? '#92400e' : '#475569';
                                return (
                                  <td key={cat} className="px-1 py-1 text-center">
                                    <span className="inline-flex items-center justify-center w-9 h-6 rounded font-semibold" style={{ background: bg, color: fg }}>
                                      {v == null ? '—' : Math.round(v)}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">Best BEE score per equipment × opportunity type. Greener = stronger opportunity.</p>
                  </div>
                )}
              </div>
            )}

            {/* Recommendations — full-width report-style rows */}
            <div className="space-y-3">
              {bestPerSeu.map((c, i) => {
                const nid = c.capex_item_id;
                const nar = narratives[nid] || {};
                const loading = narrativeLoading[nid];
                const tierConfig = CAPEX_TIERS[c.capex_tier] || {};
                const accent = tierConfig.color || '#6b7280';
                const bee = c.bee_score;
                const band = bee == null ? null : bee >= 66 ? 'good' : bee >= 40 ? 'mid' : 'low';
                const bandCls = band === 'good' ? 'bg-green-50 text-green-700 border-green-200'
                  : band === 'mid' ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-slate-50 text-slate-600 border-slate-200';
                const reasons = Array.isArray(c.reasons) ? c.reasons : [];
                return (
                  <div key={i} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                    {/* Header: rank + opportunity + score + inline metrics */}
                    <div className="flex items-start gap-3 p-4 border-l-4" style={{ borderLeftColor: accent }}>
                      <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-700 text-sm font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                          <h4 className="font-semibold text-gray-900">{c.opportunity_name}</h4>
                          <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${tierConfig.bgColor || 'bg-gray-100'} ${tierConfig.textColor || 'text-gray-700'}`}>
                            {c.capex_tier}
                          </span>
                          {c.opportunity_category && (
                            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-indigo-50 text-indigo-700">{c.opportunity_category}</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          For {c.equipment_type || c.equipment_name}{c.seu_code ? ` · ${c.seu_code}` : ''}{c.capex_item_name ? ` · ${c.capex_item_name}` : ''}
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
                          <span className="text-sm"><span className="text-[11px] text-gray-400">Savings/yr </span><span className="font-semibold text-green-700">₹{((c.estimated_annual_savings_inr || 0) / 100000).toFixed(2)}L</span></span>
                          <span className="text-sm"><span className="text-[11px] text-gray-400">Payback </span><span className="font-semibold text-blue-700">{c.simple_payback_years ? `${c.simple_payback_years} yr` : '—'}</span></span>
                          <span className="text-sm"><span className="text-[11px] text-gray-400">tCO₂e/yr </span><span className="font-semibold text-emerald-700">{(c.tco2e_reduction_annual || 0).toFixed(1)}</span></span>
                          <span className="text-sm"><span className="text-[11px] text-gray-400">CAPEX </span><span className="font-semibold text-gray-800">₹{((c.estimated_capex_low || 0) / 100000).toFixed(1)}–{((c.estimated_capex_high || 0) / 100000).toFixed(1)}L</span></span>
                        </div>
                        {reasons.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {reasons.map((r, ri) => (
                              <span key={ri} className="inline-flex items-center gap-1 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                                <CheckCircle className="w-3 h-3 text-green-500" />{r}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {bee != null && (
                        <div className={`flex flex-col items-center justify-center rounded-lg border ${bandCls} px-3 py-1.5 flex-shrink-0`} title="BEE opportunity score">
                          <div className="text-lg font-bold leading-none">{Math.round(bee)}</div>
                          <div className="text-[9px] uppercase tracking-wide mt-0.5">BEE / 100</div>
                          {c.confidence_pct != null && <div className="text-[10px] text-gray-400 mt-0.5">{Math.round(c.confidence_pct)}% conf</div>}
                        </div>
                      )}
                    </div>

                    {/* Narrative: Overview / Benefits / Implementation */}
                    <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3">
                      {loading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" /> Generating explanation…
                        </div>
                      ) : nar.error ? (
                        <p className="text-sm text-red-600">Couldn't load the explanation.</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Overview</div>
                            <p className="text-gray-700">{nar.description || '—'}</p>
                          </div>
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Benefits</div>
                            <p className="text-gray-700">{nar.how_it_helps || '—'}</p>
                          </div>
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Implementation</div>
                            <p className="text-gray-700">{nar.implementation_notes || '—'}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {viewMode !== 'recommended' && (
        <>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="opportunities-table">
            <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Rank</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">CAPEX Tier</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Equipment / SEU</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Opportunity</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">CAPEX (INR)</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">Savings/yr</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600">Payback</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">tCO₂e/yr</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">₹/tCO₂e</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600">Score</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {viewMode === 'detail' && filteredCalculations.map((calc, idx) => {
                const tierConfig = CAPEX_TIERS[calc.capex_tier] || {};
                const isCostSaving = calc.abatement_cost < 0;
                
                return (
                  <tr key={idx} className={`hover:bg-gray-50 ${idx < 3 ? 'bg-green-50/50' : ''}`}>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-orange-300 text-orange-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {calc.rank}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${tierConfig.bgColor} ${tierConfig.textColor}`}>
                        {calc.capex_tier}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{calc.equipment_type || calc.equipment_name}</div>
                      <div className="text-xs text-gray-500">{calc.seu_code}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-800">{calc.capex_item_name}</div>
                      <div className="text-xs text-gray-500">{calc.opportunity_name}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-medium">{(calc.estimated_capex_low / 1000)?.toFixed(0)}K - {(calc.estimated_capex_high / 1000)?.toFixed(0)}K</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-medium text-green-600">₹{(calc.estimated_annual_savings_inr / 100000)?.toFixed(2)}L</div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {calc.simple_payback_years ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          calc.simple_payback_years < 2 ? 'bg-green-100 text-green-700' :
                          calc.simple_payback_years < 4 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {calc.simple_payback_years} yr
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-emerald-600 font-medium">{calc.tco2e_reduction_annual?.toFixed(1)}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={`font-medium ${isCostSaving ? 'text-green-600' : 'text-orange-600'}`}>
                        {isCostSaving ? '−' : ''}₹{Math.abs(calc.abatement_cost)?.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        calc.priority_score > 1 ? 'bg-green-600 text-white' :
                        calc.priority_score > 0.5 ? 'bg-yellow-500 text-white' :
                        'bg-gray-400 text-white'
                      }`}>
                        {calc.priority_score?.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 bg-gray-50 text-center text-sm text-gray-500">
          Showing {filteredCalculations.length} of {portfolioData?.total_opportunities || filteredCalculations.length} opportunities.
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default OpportunityPortfolioV3;
